import AuthApi from "@/api/AuthApi";
import UserApi from "@/api/UserApi";
import { ActionTree } from "vuex";
import AuthStore from "@/AuthStore";
import { Logger, IRootState, User } from "@iloq/shared";
import { CorePermission, ResponseStatus } from "@iloq/shared";
import { handleCommonApiErrors } from "@/api/CommonApiErrorHandler";
import GDPRStore from "@/GDPRStore";

const authApi = new AuthApi();
const userApi = new UserApi();

const actions: ActionTree<IRootState, IRootState> = {
  saveTokens({ dispatch }, tokens: any): Promise<any> {
    AuthStore.saveRefreshToken(tokens.refreshToken);
    AuthStore.saveAuthToken(tokens.authToken);
    return dispatch("fetchUser").then((user: User) => {
      if (user.permissions.includes(CorePermission.login)) {
        authApi.extranetLogin(tokens.azureAdToken);
      }
    });
  },
  refreshAuthToken(): Promise<any> {
    const refreshToken = AuthStore.loadRefreshToken();
    if (refreshToken) {
      Logger.debug("refreshAuthToken");
      return authApi
        .fetchAuthToken(refreshToken)
        .then((token: string) => {
          AuthStore.saveAuthToken(token);
        })
        .catch(handleCommonApiErrors);
    }
    return Promise.reject(new Error("No saved refresh token"));
  },
  fetchDownloadToken(): Promise<any> {
    const refreshToken = AuthStore.loadRefreshToken();
    if (refreshToken) {
      Logger.debug("fetchDownloadToken");
      return authApi
        .fetchDownloadToken(refreshToken)
        .then((token: string) => Promise.resolve(token));
    }
    return Promise.reject(new Error("No saved refresh token"));
  },
  redirectLogin(): void {
    window.location.href = "/user/login";
  },
  fetchUser({ commit, dispatch }): Promise<any> {
    return userApi
      .fetchUser()
      .then((user: User) => {
        commit("setUser", user);
        dispatch("returns/listReturns", user.email);
        dispatch("news/fetchNews", user.languageCode);
        dispatch("news/fetchBlogPosts", user.languageCode);
        dispatch(
          "bulletin/fetchBulletin",
          user.groups.indexOf("Country_OTHER") >= 0 ? "XX" : user.countryCode, // Use 'XX' for 'OTHER' countries, as the API expects a country code. API will default to generic 'en' bulletins.
        );
        if (user.hasPermissions([CorePermission.order])) {
          dispatch("orders/fetchOrders");
        }
        dispatch("partnerInfo/getPartnerInfo", user);
        dispatch("partnerInfo/fetchDeliveryTypes", user.email);
        dispatch("material/fetchData");
        dispatch("solutions/initializeState", user.languageCode);
        return Promise.resolve(user);
      })
      .catch(handleCommonApiErrors);
  },
  async gdprConsent({ commit }, consent: boolean): Promise<void> {
    commit("hideGDPR");
    return userApi
      .gdprConsent(consent)
      .catch((err) => {
        if (err?.response?.status === ResponseStatus.conflict) {
          return;
        }
        throw err;
      })
      .then(() => {
        // since the current access tokens don't contain updated
        // AAD Group, use localStorage to store the consent until
        // user information is updated via relogin.
        GDPRStore.saveGdprConsent(consent);
      })
      .catch((err) => {
        handleCommonApiErrors(err, false, true);
      });
  },
  logout({ dispatch }): Promise<any> {
    const refreshToken = AuthStore.loadRefreshToken();
    if (refreshToken) {
      return authApi.logout(refreshToken).then(() => {
        dispatch("localLogout");
      });
    } else {
      dispatch("localLogout");
    }
    return Promise.resolve();
  },
  localLogout({ commit }): Promise<any> {
    AuthStore.removeLogin();
    commit("setUser", undefined);
    return Promise.resolve();
  },
};

export default actions;
